import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import type {
  Expense,
  ExpenseCategory,
  ExpenseInput,
  ProjectPayment,
  ProjectPaymentInput,
  ProjectPaymentPatch,
  VExpense,
  VProjectPayment,
} from "./types";

const expenseCategoriesCol = collection(db, "expense_categories");
const expensesCol = collection(db, "expenses");
const projectPaymentsCol = collection(db, "project_payments");

export async function fetchExpenseCategories(): Promise<ExpenseCategory[]> {
  const r = await getDocs(query(expenseCategoriesCol, where("ativo", "==", true)));
  return r.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<ExpenseCategory, "id">) }))
    .sort((a, b) => a.ordem - b.ordem);
}

export async function fetchExpenses(): Promise<VExpense[]> {
  const [r, categorias] = await Promise.all([
    getDocs(query(expensesCol, orderBy("data_despesa", "desc"))),
    fetchExpenseCategories(),
  ]);
  const porCategoria = new Map(categorias.map((c) => [c.id, c]));
  return r.docs.map((d) => {
    const data = d.data() as Omit<Expense, "id">;
    const categoria = porCategoria.get(data.categoria_id);
    return {
      id: d.id,
      descricao: data.descricao,
      valor: data.valor,
      data_despesa: data.data_despesa,
      recorrencia: data.recorrencia,
      categoria_codigo: categoria?.codigo ?? "outros",
      categoria_rotulo: categoria?.rotulo ?? "Outros",
      criado_por: data.criado_por,
      criado_em: data.criado_em,
      atualizado_em: data.atualizado_em,
    };
  });
}

export async function createExpense(input: ExpenseInput): Promise<Expense> {
  const agora = new Date().toISOString();
  const novo = {
    categoria_id: input.categoria_id,
    descricao: input.descricao,
    valor: input.valor,
    data_despesa: input.data_despesa ?? agora.slice(0, 10),
    recorrencia: input.recorrencia ?? "unica",
    criado_por: null,
    criado_em: agora,
    atualizado_em: agora,
  };
  const ref = await addDoc(expensesCol, novo);
  return { id: ref.id, ...novo };
}

function statusPagamento(vencimento: string, recebidoEm: string | null): VProjectPayment["status"] {
  if (recebidoEm) return "recebido";
  return vencimento < new Date().toISOString().slice(0, 10) ? "atrasado" : "pendente";
}

export async function fetchProjectPayments(): Promise<VProjectPayment[]> {
  const [r, leadsSnap, projetosSnap] = await Promise.all([
    getDocs(query(projectPaymentsCol, orderBy("vencimento", "desc"))),
    getDocs(collection(db, "leads")),
    getDocs(collection(db, "client_projects")),
  ]);
  const leadsPorId = new Map(leadsSnap.docs.map((d) => [d.id, d.data() as { empresa: string }]));
  const projetosPorId = new Map(projetosSnap.docs.map((d) => [d.id, d.data() as { lead_id: string }]));

  return r.docs.map((d) => {
    const data = d.data() as Omit<ProjectPayment, "id">;
    const leadId = projetosPorId.get(data.project_id)?.lead_id;
    const empresa = leadId ? (leadsPorId.get(leadId)?.empresa ?? "(cliente removido)") : "(cliente removido)";
    return {
      id: d.id,
      project_id: data.project_id,
      empresa,
      descricao: data.descricao,
      valor: data.valor,
      vencimento: data.vencimento,
      recebido_em: data.recebido_em,
      forma_pagamento: data.forma_pagamento,
      status: statusPagamento(data.vencimento, data.recebido_em),
      criado_por: data.criado_por,
      criado_em: data.criado_em,
    };
  });
}

/** Parcelas de UM projeto — usado pra validar a soma contra valor_fechado
 * na ficha do projeto (ver ClientProjectDrawer.tsx). Só chamado quando o
 * usuário é sócio (isSocio() em firestore.rules bloqueia o resto). */
export async function fetchProjectPaymentsByProject(projectId: string): Promise<ProjectPayment[]> {
  const r = await getDocs(query(projectPaymentsCol, where("project_id", "==", projectId)));
  return r.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ProjectPayment, "id">) }));
}

export async function createProjectPayment(input: ProjectPaymentInput): Promise<ProjectPayment> {
  const agora = new Date().toISOString();
  const novo = {
    project_id: input.project_id,
    descricao: input.descricao ?? null,
    valor: input.valor,
    vencimento: input.vencimento,
    recebido_em: null,
    forma_pagamento: input.forma_pagamento ?? null,
    criado_por: null,
    criado_em: agora,
    atualizado_em: agora,
  };
  const ref = await addDoc(projectPaymentsCol, novo);
  return { id: ref.id, ...novo };
}

export async function updateProjectPayment(
  id: string,
  patch: ProjectPaymentPatch,
): Promise<ProjectPayment> {
  const antesSnap = await getDoc(doc(db, "project_payments", id));
  if (!antesSnap.exists()) throw new Error(`Parcela ${id} não encontrada.`);

  if (patch.recebido_em && patch.recebido_em > new Date().toISOString().slice(0, 10)) {
    throw new Error("A data de recebimento não pode ser no futuro.");
  }

  await updateDoc(doc(db, "project_payments", id), { ...patch, atualizado_em: new Date().toISOString() });
  const depois = await getDoc(doc(db, "project_payments", id));
  return { id, ...(depois.data() as Omit<ProjectPayment, "id">) };
}

/** Marca uma parcela como recebida hoje — atalho para o caso comum (a data
 * exata do recebimento raramente é diferente de "agora que estou lançando"). */
export function marcarPagamentoRecebido(id: string): Promise<ProjectPayment> {
  return updateProjectPayment(id, { recebido_em: new Date().toISOString().slice(0, 10) });
}
